import { useState, useEffect, useCallback, useRef } from 'react';
import { filasDoOperador } from './mysuiteApi';
import { janela, ehDesktop } from './plataforma';

// ─────────────────────────────────────────────────────────────────────────────
// Filas do operador no MySuite — a orquestração.
//
// São os três cartões da tela de início que saem da fila de pendentes:
// "Aguardando solução" (status 1), "Em atendimento" (36) e "Setor de sistemas"
// (18). A quarta, a fila de retorno (5), tem hook próprio — ver
// `useFilaRetorno`, que carrega junto as regras de prazo. "Outros status"
// recolhe o resto: o que está no nome do operador e não cabe em nenhuma delas.
//
// POR QUE MORA NO CONTEXTO, E NÃO NA TELA
//
// Até aqui o relógio vivia dentro da Formalização e só girava com a tela de
// início aberta na fase 1. Bastava o operador entrar num atendimento, abrir a
// aba de incidentes ou recolher o app para o botão flutuante e a consulta
// parava — quando ele voltava, os cartões mostravam a foto de meia hora atrás.
//
// Aqui em cima o ciclo é um só: começa no login e só termina no logout,
// independentemente da aba aberta ou de o app estar recolhido na bandeja.
//
// ATENÇÃO AO ELECTRON: com a janela oculta o Chromium congela os `setInterval`
// da página. Por isso `backgroundThrottling: false` em electron/main.js — sem
// ele este relógio para justamente no cenário em que ele mais importa.
// ─────────────────────────────────────────────────────────────────────────────

/** De quanto em quanto tempo as filas são buscadas no MySuite. */
export const INTERVALO_FILAS_MS = 5 * 60 * 1000;

/** O estado de partida — e o que fica quando não há operador. */
const VAZIO = { aguardandoSolucao: [], emAtendimento: [], setorSistemas: [], outrosStatus: [] };

export function useFilasMysuite({ operador }) {
  const [filas, setFilas] = useState(VAZIO);
  const [carregando, setCarregando] = useState(false);
  // Instante da última consulta que deu certo. A tela usa para dizer há quanto
  // tempo os cartões foram atualizados.
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  // Trava de reentrada: a fila de pendentes é uma consulta cara e o gatilho vem
  // de três lugares (relógio, volta do foco, botão "atualizar"). Sem isto, três
  // POSTs simultâneos para a mesma resposta.
  const consultando = useRef(false);

  const codigo = operador?.codigo || null;

  const recarregar = useCallback(async () => {
    if (!codigo) { setFilas(VAZIO); return; }
    if (consultando.current) return;
    consultando.current = true;
    setCarregando(true);
    try {
      setFilas(await filasDoOperador(codigo));
      setAtualizadoEm(new Date());
    } catch (err) {
      // Falha de rede mantém o que já estava na tela: um cartão zerado por
      // engano faria o operador achar que a fila dele esvaziou.
      console.warn('[Orion] Filas do operador:', err.message);
    } finally {
      consultando.current = false;
      setCarregando(false);
    }
  }, [codigo]);

  // O relógio: busca no login e depois a cada 5 minutos.
  useEffect(() => {
    if (!codigo) { setFilas(VAZIO); setAtualizadoEm(null); return undefined; }
    recarregar();
    const id = setInterval(recarregar, INTERVALO_FILAS_MS);
    return () => clearInterval(id);
  }, [codigo, recarregar]);

  // Voltar ao app pede uma consulta na hora. Cobre o buraco que o relógio não
  // cobre: a máquina que dormiu, a janela que ficou horas oculta, o operador
  // que reabre depois do almoço e quer o quadro de agora — não o de antes.
  useEffect(() => {
    if (!codigo) return undefined;

    const aoVoltar = () => { if (!document.hidden) recarregar(); };

    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    // No desktop a janela some para a bandeja sem disparar 'focus': o processo
    // principal avisa por 'app-restored'.
    const pararRestauracao = ehDesktop ? janela.aoRestaurar(recarregar) : null;

    return () => {
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      if (typeof pararRestauracao === 'function') pararRestauracao();
    };
  }, [codigo, recarregar]);

  return { filas, carregando, atualizadoEm, recarregar };
}
